import { useTranslation } from 'react-i18next';
import { cn, Tooltip } from '@c2fo/liquidity';
import { InfoCircleIcon, PenIcon } from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import RateDisplay from '@/enterprise/features/homepage/marketTable/RateDisplay';
import ParticipationSwitch from '@/enterprise/features/participationSwitch/ParticipationSwitch';
import RenewalRequestIcon from '@/enterprise/features/preferred/RenewalRequestIcon';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import getRenewalSchedule from '@/utils/getRenewalSchedule';
import { isPreferredOfferNearTermEnd } from '@/utils/preferredOfferRenewal';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useRestrictions from '@/utils/useRestrictions';

const OfferContent = ({
  hasOfferSchedule,
  openOfferModal,
  takerMarket,
}: {
  hasOfferSchedule: boolean;
  openOfferModal: () => void;
  takerMarket: TakerMarket;
}) => {
  const { t } = useTranslation();
  const { asDateStringText } = useLocaleFormat();
  const { getRestrictions } = useRestrictions();
  const { canEditOffers } = getRestrictions([takerMarket]);
  const { hasRenewalSchedule } = getRenewalSchedule(takerMarket);

  const offerType =
    takerMarket.offerConfig.offerType === 'PREFERRED_TERM'
      ? 'PREFERRED'
      : takerMarket.marketPricingType === 'BENCHMARK'
      ? 'BENCHMARK'
      : takerMarket.marketPricingType === 'STATIC'
      ? 'STATIC'
      : 'NAME_YOUR_RATE';
  const isOneTimeOffer = takerMarket.offerConfig.frequency === 'ONE_TIME';
  const isExpressAccept = isOneTimeOffer && takerMarket.offerConfig.offerType === 'EXPRESS_ACCEPT';
  const showFrequency = offerType !== 'PREFERRED' && !hasOfferSchedule && takerMarket.offerConfig.isEnabled;

  const offerContent = (
    <div className="flex flex-wrap gap-4 lg:gap-10">
      {offerType === 'PREFERRED' && takerMarket.disableAfterMarketClearsDate ? (
        <div className="shrink-0">
          <div>{t('taker.termEnd')}</div>
          <div className="flex h-8 items-center gap-1 text-lg font-bold">
            <span className={cn({ 'text-red-700': isPreferredOfferNearTermEnd(takerMarket) && !hasRenewalSchedule })}>
              {asDateStringText({ date: takerMarket.disableAfterMarketClearsDate, isUTC: false })}
            </span>
            <RenewalRequestIcon iconStyles="fill-red-700" takerMarket={takerMarket} />
          </div>
        </div>
      ) : null}
      {showFrequency ? (
        <div className="shrink-0">
          <div>{t('builder.frequency')}</div>
          <div className="flex h-8 items-center gap-1 text-lg font-bold">
            {isExpressAccept
              ? t('core.oneTimeExpressAccept')
              : isOneTimeOffer
              ? t('builder.oneTime')
              : t('builder.recurring')}
            <Tooltip>
              <Tooltip.Trigger aria-label="Frequency Tooltip">
                <InfoCircleIcon className="h-4 w-4 fill-text-primary" />
              </Tooltip.Trigger>
              <Tooltip.Content
                arrow
                className="max-w-40 whitespace-pre-wrap text-center font-normal"
                collisionPadding={24}
                side="bottom"
                variant="dark"
              >
                {isOneTimeOffer ? t('builder.oneTimeInfo') : t('builder.recurringInfo')}
              </Tooltip.Content>
            </Tooltip>
          </div>
        </div>
      ) : null}
      <div className="shrink-0">
        <div>{t('taker.offer')}</div>
        <div className="flex h-8 items-center gap-1 text-lg font-bold">
          <RateDisplay showDiscountAmount={false} takerMarket={takerMarket} />
          {offerType === 'NAME_YOUR_RATE' && (
            <button
              aria-label={t('builder.editRate')}
              className="flex cursor-pointer items-center rounded-full p-2 hover:bg-black/10 focus:outline-none active:outline-none disabled:opacity-38 disabled:hover:cursor-default disabled:hover:bg-transparent"
              disabled={!canEditOffers || !takerMarket.marketIsEnabled}
              onClick={openOfferModal}
              type="button"
            >
              <PenIcon className="h-6 w-6 fill-text-secondary" />
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col items-center">
        <div>{t('core.participation')}</div>
        <div className="flex h-8 items-center">
          <ParticipationSwitch takerMarket={takerMarket} withLabel />
        </div>
      </div>
    </div>
  );

  return offerType === 'NAME_YOUR_RATE' ? (
    <IntercomDataTarget className="flex shrink-0" target="nameYourRateBannerActions">
      {offerContent}
    </IntercomDataTarget>
  ) : (
    offerContent
  );
};

export default OfferContent;
